import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Badge,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  PersonAdd as ActivateIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { useSnackbar } from "notistack";
import { superAdminAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { calculateProfileCompletion } from "../utils/profileCompletion";

const Contractors = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTenant, setFilterTenant] = useState("all");
  const [tenants, setTenants] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [approvingContractorId, setApprovingContractorId] = useState(null);
  const [loginCredentials, setLoginCredentials] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "pending", label: "Pending Approval" },
    { value: "suspended", label: "Suspended" },
  ];

  const tabs = [
    { label: "All Contractors", value: 0 },
    { label: "Active", value: 1 },
    { label: "Pending Approval", value: 2 },
    { label: "License Expiring", value: 3 },
  ];

  const steps = [
    "Basic Information",
    "Owner Details",
    "License Information",
    "Document Upload",
    "User Creation",
    "Review & Submit",
  ];

  const [formData, setFormData] = useState({
    // Basic Information
    companyName: "",
    registrationNumber: "",
    gstNumber: "",
    panNumber: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    contactDetails: {
      primaryContact: {
        name: "",
        phone: "",
        email: "",
      },
      secondaryContact: {
        name: "",
        phone: "",
        email: "",
      },
    },
    businessDetails: {
      businessType: "",
      industry: "",
      establishedYear: "",
      employeeCount: "",
      annualTurnover: "",
    },

    // Owner Details
    ownerDetails: {
      name: "",
      aadhaarNumber: "",
      panNumber: "",
      phone: "",
      email: "",
      address: {
        street: "",
        city: "",
        state: "",
        pincode: "",
      },
    },

    // License Information
    licenseDetails: {
      laborLicenseNumber: "",
      licenseIssuedBy: "",
      licenseIssueDate: null,
      licenseExpiryDate: null,
      licenseStatus: "active",
      licenseType: "principal_employer",
    },

    // Document Upload
    documents: {
      registrationCertificate: null,
      gstCertificate: null,
      panCard: null,
      laborLicense: null,
      bankDetails: null,
      agreementDocument: null,
    },

    // User Creation
    userCreation: {
      createUser: false,
      username: "",
      email: "",
      password: "",
      role: "contractor",
      permissions: [],
    },
  });

  const loadTenants = useCallback(async () => {
    if (user?.role !== "superadmin") return;

    try {
      const response = await superAdminAPI.getTenants({
        page: 1,
        limit: 100,
        status: "all",
      });

      if (response.data.success) {
        setTenants(response.data.data?.tenants || []);
      }
    } catch (error) {
      console.error("Failed to load tenants:", error);
    }
  }, [user?.role]);

  const loadContractors = useCallback(async () => {
    try {
      setLoading(true);

      // Use superadmin API if user is superadmin, otherwise use regular API
      const response =
        user?.role === "superadmin"
          ? await superAdminAPI.getContractors({
              page: 1,
              limit: 50,
              search: searchTerm,
              status: filterStatus,
              tenantId: filterTenant !== "all" ? filterTenant : undefined,
              sortBy: "createdAt",
              sortOrder: "desc",
            })
          : await axios.get("/api/contractors", {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "x-tenant-id": localStorage.getItem("tenantId"),
              },
              params: {
                page: 1,
                limit: 50,
                search: searchTerm,
                status: filterStatus,
                sortBy: "createdAt",
                sortOrder: "desc",
              },
            });
      // Handle the API response structure
      if (response.data.success) {
        const contractorsData = response.data.data?.contractors || [];

        // If contractors don't have user data, try to fetch it
        const contractorsWithUsers = await Promise.all(
          contractorsData.map(async (contractor) => {
            // If contractor has a user ID but no user object, fetch user details
            if (
              (contractor.userId || contractor.user?.id) &&
              !contractor.user
            ) {
              try {
                const userId = contractor.userId || contractor.user?.id;
                const userResponse = await axios.get(
                  `/api/contractor-details/users/${userId}`,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      "x-tenant-id": localStorage.getItem("tenantId"),
                    },
                  }
                );
                if (userResponse.data.success) {
                  contractor.user = userResponse.data.data;
                }
              } catch (error) {
                // No user found for this contractor
              }
            }
            return contractor;
          })
        );

        setContractors(contractorsWithUsers);
      } else {
        setContractors([]);
        enqueueSnackbar("Failed to load contractors", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to load contractors", { variant: "error" });
      console.error("Error loading contractors:", error);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, searchTerm, filterStatus, filterTenant, user?.role]);

  useEffect(() => {
    loadContractors();
    loadTenants();
  }, [loadContractors, loadTenants]);

  const handleCreateContractor = () => {
    setSelectedContractor(null);
    setOpenForm(true);
    setActiveStep(0);
  };

  const handleEditContractor = async (contractor) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/contractors/${contractor._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        },
      });
      if (response.data?.success) {
        const c = response.data.data;
        // Prefill form data mapping
        setFormData((prev) => ({
          ...prev,
          companyName: c.companyName || "",
          registrationNumber: c.registrationNumber || "",
          gstNumber: c.registrationDetails?.gstNumber || c.gstNumber || "",
          panNumber: c.registrationDetails?.panNumber || c.panNumber || "",
          address: {
            street: c.contactDetails?.address?.registered?.street || "",
            city: c.contactDetails?.address?.registered?.city || "",
            state: c.contactDetails?.address?.registered?.state || "",
            pincode: c.contactDetails?.address?.registered?.pincode || "",
            country: c.contactDetails?.address?.registered?.country || "India",
          },
          contactDetails: {
            primaryContact: {
              name: c.contactDetails?.primaryContact?.name || "",
              phone: c.contactDetails?.primaryContact?.phone || "",
              email: c.contactDetails?.primaryContact?.email || "",
            },
            secondaryContact: prev.contactDetails.secondaryContact,
          },
          ownerDetails: {
            name: c.ownerDetails?.ownerName || "",
            aadhaarNumber: c.ownerDetails?.aadhaarNumber || "",
            panNumber: c.ownerDetails?.panNumber || "",
            phone: c.ownerDetails?.phoneNumber || "",
            email: c.ownerDetails?.emailAddress || "",
            address: {
              street: prev.ownerDetails.address.street,
              city: prev.ownerDetails.address.city,
              state: prev.ownerDetails.address.state,
              pincode: prev.ownerDetails.address.pincode,
            },
          },
          licenseDetails: {
            laborLicenseNumber: c.licenseDetails?.laborLicenseNumber || "",
            licenseIssuedBy: c.licenseDetails?.issuingAuthority || "",
            licenseIssueDate: c.licenseDetails?.issueDate || null,
            licenseExpiryDate: c.licenseDetails?.validTill || null,
            licenseStatus: prev.licenseDetails.licenseStatus,
            licenseType: c.licenseDetails?.licenseType || "principal_employer",
          },
        }));
      }
    } catch (e) {
      enqueueSnackbar("Failed to load contractor details", {
        variant: "error",
      });
    } finally {
      setLoading(false);
      setSelectedContractor(contractor);
      setOpenForm(true);
      setActiveStep(0);
    }
  };

  const handleViewContractor = (contractor) => {
    setSelectedContractor(contractor);
    setOpenViewDialog(true);
  };

  const handleDeleteContractor = async (contractorId) => {
    if (user?.role === "superadmin") {
      // For superadmin, show options for soft delete or hard delete
      const deleteType = window.confirm(
        "Choose delete type:\n\n" +
          "OK = Soft Delete (deactivate)\n" +
          "Cancel = Hard Delete (permanent removal)\n\n" +
          "Are you sure you want to delete this contractor?"
      );

      try {
        if (deleteType) {
          // Soft delete (deactivate)
          await superAdminAPI.deleteContractor(contractorId, false);
          enqueueSnackbar("Contractor deactivated successfully", {
            variant: "success",
          });
        } else {
          // Hard delete (permanent removal)
          const confirmHardDelete = window.confirm(
            "⚠️ WARNING: This will permanently delete the contractor and all associated data!\n\n" +
              "This action cannot be undone. Are you absolutely sure?"
          );

          if (confirmHardDelete) {
            await superAdminAPI.deleteContractor(contractorId, true);
            enqueueSnackbar("Contractor permanently deleted", {
              variant: "success",
            });
          } else {
            return; // User cancelled hard delete
          }
        }
        loadContractors();
      } catch (error) {
        console.error("Error deleting contractor:", error);
        const errorMessage =
          error.response?.data?.error || "Failed to delete contractor";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    } else {
      // For regular users, use the standard delete
      if (window.confirm("Are you sure you want to delete this contractor?")) {
        try {
          await axios.delete(`/api/contractors/${contractorId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "x-tenant-id": localStorage.getItem("tenantId"),
            },
          });
          enqueueSnackbar("Contractor deleted successfully", {
            variant: "success",
          });
          loadContractors();
        } catch (error) {
          console.error("Error deleting contractor:", error);
          enqueueSnackbar("Failed to delete contractor", { variant: "error" });
        }
      }
    }
  };

  const handleActivateContractor = async (contractorId) => {
    // Find the contractor to check profile completion
    const contractor = contractors.find((c) => c._id === contractorId);
    const profileStatus = calculateProfileCompletion(contractor);
    const profileComplete = profileStatus.isComplete;
    const profileProgress = profileStatus.completionPercentage;

    if (!profileComplete) {
      enqueueSnackbar(
        `Cannot activate contractor. Profile is only ${profileProgress}% complete. Please complete the profile first.`,
        { variant: "warning" }
      );
      return;
    }

    if (window.confirm("Are you sure you want to activate this contractor?")) {
      try {
        setApprovingContractorId(contractorId);
        await axios.patch(
          `/api/contractor-details/${contractorId}/status`,
          {
            status: "active",
            reason: "Approved by admin",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "x-tenant-id": localStorage.getItem("tenantId"),
              "Content-Type": "application/json",
            },
          }
        );
        enqueueSnackbar("Contractor activated successfully", {
          variant: "success",
        });
        loadContractors();
      } catch (error) {
        const msg =
          error.response?.data?.message || "Failed to activate contractor";
        enqueueSnackbar(msg, { variant: "error" });
      } finally {
        setApprovingContractorId(null);
      }
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedContractor(null);
    setActiveStep(0);
  };

  const showLoginCredentialsModal = (user) => {
    setLoginCredentials(user);
    setShowCredentialsModal(true);
  };

  const closeCredentialsModal = () => {
    setShowCredentialsModal(false);
    setLoginCredentials(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      enqueueSnackbar("Copied to clipboard!", { variant: "success" });
    });
  };

  const handleFormSave = async () => {
    try {
      setSaving(true);
      // Build payload per backend spec
      let payload;

      if (selectedContractor?._id) {
        // For editing, use the full payload
        payload = {
          contractorId: selectedContractor?.contractorId || undefined,
          companyName: formData.companyName,
          status: "pending", // New contractors start as pending
          registrationDetails: {
            panNumber: formData.panNumber,
            gstNumber: formData.gstNumber,
            clraDetails: {
              rcNumber: formData.registrationNumber || undefined,
              issueDate: formData.licenseDetails.licenseIssueDate
                ? dayjs(formData.licenseDetails.licenseIssueDate).format(
                    "YYYY-MM-DD"
                  )
                : undefined,
              expiryDate: formData.licenseDetails.licenseExpiryDate
                ? dayjs(formData.licenseDetails.licenseExpiryDate).format(
                    "YYYY-MM-DD"
                  )
                : undefined,
              issuingAuthority:
                formData.licenseDetails.licenseIssuedBy || undefined,
              validStates: formData.address.state
                ? [formData.address.state]
                : undefined,
            },
            businessType:
              (formData.businessDetails?.businessType || "").toLowerCase() ||
              undefined,
          },
          contactDetails: {
            primaryContact: {
              name: formData.contactDetails.primaryContact.name,
              phone: formData.contactDetails.primaryContact.phone,
              email: formData.contactDetails.primaryContact.email,
            },
            address: {
              registered: {
                street: formData.address.street,
                city: formData.address.city,
                state: formData.address.state,
                pincode: formData.address.pincode,
              },
            },
          },
          ownerDetails: {
            ownerName: formData.ownerDetails.name,
            aadhaarNumber: formData.ownerDetails.aadhaarNumber,
            panNumber: formData.ownerDetails.panNumber,
            phoneNumber: formData.ownerDetails.phone,
            emailAddress: formData.ownerDetails.email,
          },
          licenseDetails: {
            laborLicenseNumber: formData.licenseDetails.laborLicenseNumber,
            issuingAuthority: formData.licenseDetails.licenseIssuedBy,
            issueDate: formData.licenseDetails.licenseIssueDate
              ? dayjs(formData.licenseDetails.licenseIssueDate).format(
                  "YYYY-MM-DD"
                )
              : undefined,
            validTill: formData.licenseDetails.licenseExpiryDate
              ? dayjs(formData.licenseDetails.licenseExpiryDate).format(
                  "YYYY-MM-DD"
                )
              : undefined,
            licenseType:
              formData.licenseDetails.licenseType === "principal_employer"
                ? "Principal Employer"
                : formData.licenseDetails.licenseType,
          },
        };
      } else {
        // For creating new contractor, only send the 4 required fields
        payload = {
          companyName: formData.companyName,
          contactName: formData.contactDetails.primaryContact.name,
          contactEmail: formData.contactDetails.primaryContact.email,
          contactPhone: formData.contactDetails.primaryContact.phone,
        };
      }

      // Optionally include user
      if (formData.userCreation.createUser) {
        payload.createUser = "true";
        payload.userDetails = {
          username: formData.userCreation.username,
          email: formData.userCreation.email,
          password: formData.userCreation.password,
          firstName: formData.userCreation.username?.split(".")?.[0] || "",
          lastName: formData.userCreation.username?.split(".")?.[1] || "",
          phone: formData.contactDetails.primaryContact.phone,
          role:
            formData.userCreation.role === "contractor"
              ? "contractor_user"
              : formData.userCreation.role,
          status: "pending", // New users start as pending
        };
      }

      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "x-tenant-id": localStorage.getItem("tenantId"),
        "Content-Type": "application/json",
      };

      if (selectedContractor?._id) {
        // Update contractor (send only changed simple fields for now)
        const updateBody = {
          companyName: payload.companyName,
          registrationNumber: formData.registrationNumber,
          gstNumber: payload.registrationDetails.gstNumber,
          panNumber: payload.registrationDetails.panNumber,
          contactDetails: payload.contactDetails,
          licenseDetails: payload.licenseDetails,
          ownerDetails: payload.ownerDetails,
        };
        await axios.put(
          `/api/contractors/${selectedContractor._id}`,
          updateBody,
          { headers }
        );
        enqueueSnackbar("Contractor updated", { variant: "success" });
      } else {
        // Create contractor using simplified endpoint
        const response = await axios.post("/api/contractors", payload, {
          headers,
        });

        if (response.data.success) {
          enqueueSnackbar("Contractor created successfully", {
            variant: "success",
          });

          // Show login credentials modal if user was created
          if (response.data.user) {
            showLoginCredentialsModal(response.data.user);
          }
        } else {
          enqueueSnackbar(
            response.data.message || "Failed to create contractor",
            { variant: "error" }
          );
        }
      }

      setOpenForm(false);
      setSelectedContractor(null);
      setActiveStep(0);
      await loadContractors();
    } catch (error) {
      console.error("Error saving contractor:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to save contractor";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getFilteredContractors = () => {
    let filtered = contractors;

    // Tab filtering
    switch (tabValue) {
      case 1: // Active
        filtered = filtered.filter((c) => c.status === "active");
        break;
      case 2: // Pending Approval
        filtered = filtered.filter((c) => c.status === "pending");
        break;
      case 3: // License Expiring
        filtered = filtered.filter((c) => {
          const expiryDate = c.licenseDetails?.licenseExpiryDate;
          if (!expiryDate) return false;
          const daysUntilExpiry = dayjs(expiryDate).diff(dayjs(), "days");
          return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
        });
        break;
      default:
        break;
    }

    // Search filtering
    if (searchTerm) {
      filtered = filtered.filter(
        (c) =>
          c.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.registrationNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          c.gstNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.contactDetails?.primaryContact?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Status filtering
    if (filterStatus !== "all") {
      filtered = filtered.filter((c) => c.status === filterStatus);
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      case "pending":
        return "warning";
      case "suspended":
        return "error";
      default:
        return "default";
    }
  };

  const getUserStatus = (contractor) => {
    // Check multiple possible field names for user status
    const userStatus =
      contractor.user?.status ||
      contractor.userStatus ||
      contractor.contractorUser?.status ||
      contractor.users?.[0]?.status;

    return userStatus || "No User";
  };

  const getUserStatusColor = (contractor) => {
    const userStatus = getUserStatus(contractor);

    if (userStatus === "approved" || userStatus === "active") return "success";
    if (userStatus === "pending") return "warning";
    return "default";
  };

  const getUserStatusLabel = (contractor) => {
    const userStatus = getUserStatus(contractor);

    if (userStatus === "approved") return "Approved";
    if (userStatus === "pending") return "Pending";
    if (userStatus === "active") return "Active";
    return "No User";
  };

  const getLicenseStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "expired":
        return "error";
      case "expiring_soon":
        return "warning";
      default:
        return "default";
    }
  };

  const renderBasicInfoStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Company Name *"
          value={formData.companyName}
          onChange={(e) =>
            setFormData({ ...formData, companyName: e.target.value })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Registration Number *"
          value={formData.registrationNumber}
          onChange={(e) =>
            setFormData({ ...formData, registrationNumber: e.target.value })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="GST Number *"
          value={formData.gstNumber}
          onChange={(e) =>
            setFormData({ ...formData, gstNumber: e.target.value })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="PAN Number *"
          value={formData.panNumber}
          onChange={(e) =>
            setFormData({ ...formData, panNumber: e.target.value })
          }
          required
        />
      </Grid>

      {/* Address */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Company Address
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Street Address"
          value={formData.address.street}
          onChange={(e) =>
            setFormData({
              ...formData,
              address: { ...formData.address, street: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="City"
          value={formData.address.city}
          onChange={(e) =>
            setFormData({
              ...formData,
              address: { ...formData.address, city: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="State"
          value={formData.address.state}
          onChange={(e) =>
            setFormData({
              ...formData,
              address: { ...formData.address, state: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Pincode"
          value={formData.address.pincode}
          onChange={(e) =>
            setFormData({
              ...formData,
              address: { ...formData.address, pincode: e.target.value },
            })
          }
        />
      </Grid>

      {/* Contact Details */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Contact Details
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Primary Contact Name"
          value={formData.contactDetails.primaryContact.name}
          onChange={(e) =>
            setFormData({
              ...formData,
              contactDetails: {
                ...formData.contactDetails,
                primaryContact: {
                  ...formData.contactDetails.primaryContact,
                  name: e.target.value,
                },
              },
            })
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Primary Contact Phone"
          value={formData.contactDetails.primaryContact.phone}
          onChange={(e) =>
            setFormData({
              ...formData,
              contactDetails: {
                ...formData.contactDetails,
                primaryContact: {
                  ...formData.contactDetails.primaryContact,
                  phone: e.target.value,
                },
              },
            })
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Primary Contact Email"
          type="email"
          value={formData.contactDetails.primaryContact.email}
          onChange={(e) =>
            setFormData({
              ...formData,
              contactDetails: {
                ...formData.contactDetails,
                primaryContact: {
                  ...formData.contactDetails.primaryContact,
                  email: e.target.value,
                },
              },
            })
          }
        />
      </Grid>
    </Grid>
  );

  const renderOwnerDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Owner Name *"
          value={formData.ownerDetails.name}
          onChange={(e) =>
            setFormData({
              ...formData,
              ownerDetails: { ...formData.ownerDetails, name: e.target.value },
            })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Aadhaar Number *"
          value={formData.ownerDetails.aadhaarNumber}
          onChange={(e) =>
            setFormData({
              ...formData,
              ownerDetails: {
                ...formData.ownerDetails,
                aadhaarNumber: e.target.value,
              },
            })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="PAN Number *"
          value={formData.ownerDetails.panNumber}
          onChange={(e) =>
            setFormData({
              ...formData,
              ownerDetails: {
                ...formData.ownerDetails,
                panNumber: e.target.value,
              },
            })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.ownerDetails.phone}
          onChange={(e) =>
            setFormData({
              ...formData,
              ownerDetails: { ...formData.ownerDetails, phone: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.ownerDetails.email}
          onChange={(e) =>
            setFormData({
              ...formData,
              ownerDetails: { ...formData.ownerDetails, email: e.target.value },
            })
          }
        />
      </Grid>
    </Grid>
  );

  const renderLicenseStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Labor License Number *"
          value={formData.licenseDetails.laborLicenseNumber}
          onChange={(e) =>
            setFormData({
              ...formData,
              licenseDetails: {
                ...formData.licenseDetails,
                laborLicenseNumber: e.target.value,
              },
            })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="License Issued By *"
          value={formData.licenseDetails.licenseIssuedBy}
          onChange={(e) =>
            setFormData({
              ...formData,
              licenseDetails: {
                ...formData.licenseDetails,
                licenseIssuedBy: e.target.value,
              },
            })
          }
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="License Issue Date *"
          value={
            formData.licenseDetails.licenseIssueDate
              ? dayjs(formData.licenseDetails.licenseIssueDate)
              : null
          }
          onChange={(date) =>
            setFormData({
              ...formData,
              licenseDetails: {
                ...formData.licenseDetails,
                licenseIssueDate: date?.toDate(),
              },
            })
          }
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="License Expiry Date *"
          value={
            formData.licenseDetails.licenseExpiryDate
              ? dayjs(formData.licenseDetails.licenseExpiryDate)
              : null
          }
          onChange={(date) =>
            setFormData({
              ...formData,
              licenseDetails: {
                ...formData.licenseDetails,
                licenseExpiryDate: date?.toDate(),
              },
            })
          }
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>License Type</InputLabel>
          <Select
            value={formData.licenseDetails.licenseType}
            onChange={(e) =>
              setFormData({
                ...formData,
                licenseDetails: {
                  ...formData.licenseDetails,
                  licenseType: e.target.value,
                },
              })
            }
            label="License Type"
          >
            <MenuItem value="principal_employer">Principal Employer</MenuItem>
            <MenuItem value="contractor">Contractor</MenuItem>
            <MenuItem value="sub_contractor">Sub Contractor</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderDocumentsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info">
          Upload all required documents. Supported formats: JPG, PNG, PDF (Max
          size: 10MB)
        </Alert>
      </Grid>

      {Object.keys(formData.documents).map((docType) => (
        <Grid item xs={12} sm={6} key={docType}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {docType
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </Typography>
              <input
                accept="image/*,application/pdf"
                style={{ display: "none" }}
                id={`${docType}-upload`}
                type="file"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    documents: {
                      ...formData.documents,
                      [docType]: e.target.files[0],
                    },
                  })
                }
              />
              <label htmlFor={`${docType}-upload`}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  Upload{" "}
                  {docType
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </Button>
              </label>
              {formData.documents[docType] && (
                <Chip
                  label={formData.documents[docType].name}
                  color="success"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderUserCreationStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.userCreation.createUser}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  userCreation: {
                    ...formData.userCreation,
                    createUser: e.target.checked,
                  },
                })
              }
            />
          }
          label="Create User Account for Contractor"
        />
      </Grid>

      {formData.userCreation.createUser && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username *"
              value={formData.userCreation.username}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  userCreation: {
                    ...formData.userCreation,
                    username: e.target.value,
                  },
                })
              }
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={formData.userCreation.email}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  userCreation: {
                    ...formData.userCreation,
                    email: e.target.value,
                  },
                })
              }
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Password *"
              type="password"
              value={formData.userCreation.password}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  userCreation: {
                    ...formData.userCreation,
                    password: e.target.value,
                  },
                })
              }
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.userCreation.role}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    userCreation: {
                      ...formData.userCreation,
                      role: e.target.value,
                    },
                  })
                }
                label="Role"
              >
                <MenuItem value="contractor">Contractor</MenuItem>
                <MenuItem value="contractor_admin">Contractor Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </>
      )}
    </Grid>
  );

  const renderReviewStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Review Contractor Information
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Typography variant="body2">
              <strong>Company:</strong> {formData.companyName}
            </Typography>
            <Typography variant="body2">
              <strong>Registration:</strong> {formData.registrationNumber}
            </Typography>
            <Typography variant="body2">
              <strong>GST:</strong> {formData.gstNumber}
            </Typography>
            <Typography variant="body2">
              <strong>PAN:</strong> {formData.panNumber}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Owner Details
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {formData.ownerDetails.name}
            </Typography>
            <Typography variant="body2">
              <strong>Aadhaar:</strong> {formData.ownerDetails.aadhaarNumber}
            </Typography>
            <Typography variant="body2">
              <strong>PAN:</strong> {formData.ownerDetails.panNumber}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              License Information
            </Typography>
            <Typography variant="body2">
              <strong>License No:</strong>{" "}
              {formData.licenseDetails.laborLicenseNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Issued By:</strong>{" "}
              {formData.licenseDetails.licenseIssuedBy}
            </Typography>
            <Typography variant="body2">
              <strong>Expiry:</strong>{" "}
              {formData.licenseDetails.licenseExpiryDate
                ? dayjs(formData.licenseDetails.licenseExpiryDate).format(
                    "DD/MM/YYYY"
                  )
                : "N/A"}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Creation
            </Typography>
            <Typography variant="body2">
              <strong>Create User:</strong>{" "}
              {formData.userCreation.createUser ? "Yes" : "No"}
            </Typography>
            {formData.userCreation.createUser && (
              <>
                <Typography variant="body2">
                  <strong>Username:</strong> {formData.userCreation.username}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {formData.userCreation.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Role:</strong> {formData.userCreation.role}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderBasicInfoStep();
      case 1:
        return renderOwnerDetailsStep();
      case 2:
        return renderLicenseStep();
      case 3:
        return renderDocumentsStep();
      case 4:
        return renderUserCreationStep();
      case 5:
        return renderReviewStep();
      default:
        return "Unknown step";
    }
  };

  const filteredContractors = getFilteredContractors();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Contractor Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateContractor}
          >
            Add Contractor
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BusinessIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{contractors.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Contractors
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {contractors.filter((c) => c.status === "active").length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {contractors.filter((c) => c.status === "pending").length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending Approval
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CancelIcon color="error" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {
                        contractors.filter((c) => {
                          const expiryDate =
                            c.licenseDetails?.licenseExpiryDate;
                          if (!expiryDate) return false;
                          const daysUntilExpiry = dayjs(expiryDate).diff(
                            dayjs(),
                            "days"
                          );
                          return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
                        }).length
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      License Expiring
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  placeholder="Search contractors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {user?.role === "superadmin" && (
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Tenant</InputLabel>
                    <Select
                      value={filterTenant}
                      onChange={(e) => setFilterTenant(e.target.value)}
                      label="Tenant"
                    >
                      <MenuItem value="all">All Tenants</MenuItem>
                      {tenants.map((tenant) => (
                        <MenuItem key={tenant._id} value={tenant._id}>
                          {tenant.name} ({tenant.domain})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={user?.role === "superadmin" ? 2 : 5}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" startIcon={<FilterIcon />}>
                    More Filters
                  </Button>
                  <Button variant="outlined" startIcon={<DownloadIcon />}>
                    Export
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Badge
                    badgeContent={
                      tab.value === 0
                        ? contractors.length
                        : tab.value === 1
                        ? contractors.filter((c) => c.status === "active")
                            .length
                        : tab.value === 2
                        ? contractors.filter((c) => c.status === "pending")
                            .length
                        : contractors.filter((c) => {
                            const expiryDate =
                              c.licenseDetails?.licenseExpiryDate;
                            if (!expiryDate) return false;
                            const daysUntilExpiry = dayjs(expiryDate).diff(
                              dayjs(),
                              "days"
                            );
                            return (
                              daysUntilExpiry <= 30 && daysUntilExpiry >= 0
                            );
                          }).length
                    }
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                }
              />
            ))}
          </Tabs>
        </Card>

        {/* Contractors Table */}
        <Card>
          <CardContent>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Contractor</TableCell>
                    <TableCell>Registration</TableCell>
                    <TableCell>GST Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>User Status</TableCell>
                    <TableCell>Profile Progress</TableCell>
                    {user?.role === "superadmin" && (
                      <TableCell>Tenant</TableCell>
                    )}
                    <TableCell>License Status</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContractors.map((contractor) => (
                    <TableRow key={contractor._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            {contractor.companyName?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {contractor.companyName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {contractor.contactDetails?.primaryContact?.name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {contractor.registrationNumber || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {contractor.gstNumber || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contractor.status || "N/A"}
                          color={getStatusColor(contractor.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label={getUserStatusLabel(contractor)}
                            color={getUserStatusColor(contractor)}
                            size="small"
                          />
                          {getUserStatus(contractor) === "pending" &&
                            contractor.status === "active" && (
                              <Chip
                                label="Ready to Approve"
                                color="info"
                                size="small"
                                variant="outlined"
                              />
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={
                              calculateProfileCompletion(contractor)
                                .completionPercentage
                            }
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                            color={
                              calculateProfileCompletion(contractor)
                                .completionPercentage >= 100
                                ? "success"
                                : calculateProfileCompletion(contractor)
                                    .completionPercentage >= 70
                                ? "warning"
                                : "error"
                            }
                          />
                          <Typography variant="caption" sx={{ minWidth: 35 }}>
                            {
                              calculateProfileCompletion(contractor)
                                .completionPercentage
                            }
                            %
                          </Typography>
                        </Box>
                        {calculateProfileCompletion(contractor).isComplete && (
                          <Chip
                            label="Complete"
                            size="small"
                            color="success"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      {user?.role === "superadmin" && (
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {contractor.tenantId?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {contractor.tenantId?.domain || ""}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={
                            contractor.licenseDetails?.licenseStatus || "N/A"
                          }
                          color={getLicenseStatusColor(
                            contractor.licenseDetails?.licenseStatus
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contractor.licenseDetails?.licenseExpiryDate
                            ? dayjs(
                                contractor.licenseDetails.licenseExpiryDate
                              ).format("DD/MM/YYYY")
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewContractor(contractor)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>

                          {contractor.status === "pending" && (
                            <Tooltip
                              title={
                                calculateProfileCompletion(contractor)
                                  .isComplete
                                  ? "Activate Contractor"
                                  : `Profile incomplete (${
                                      calculateProfileCompletion(contractor)
                                        .completionPercentage
                                    }%). Complete profile first.`
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleActivateContractor(contractor._id)
                                  }
                                  color="success"
                                  disabled={
                                    approvingContractorId === contractor._id ||
                                    !calculateProfileCompletion(contractor)
                                      .isComplete
                                  }
                                >
                                  {approvingContractorId === contractor._id ? (
                                    <Box sx={{ width: 16, height: 16 }}>
                                      <LinearProgress />
                                    </Box>
                                  ) : (
                                    <ActivateIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          <Tooltip title="Edit Contractor">
                            <IconButton
                              size="small"
                              onClick={() => handleEditContractor(contractor)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title={
                              user?.role === "superadmin"
                                ? "Delete Contractor (Soft/Hard Delete)"
                                : "Delete Contractor"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteContractor(contractor._id)
                              }
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredContractors.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No contractors found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search or filter criteria
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Contractor Form Dialog */}
        <Dialog
          open={openForm}
          onClose={handleFormClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {selectedContractor ? "Edit Contractor" : "Add New Contractor"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ width: "100%", mt: 2 }}>
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {renderStepContent(activeStep)}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFormClose}>Cancel</Button>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                onClick={handleFormSave}
                variant="contained"
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : selectedContractor
                  ? "Update Contractor"
                  : "Create Contractor"}
              </Button>
            ) : (
              <Button onClick={handleNext} variant="contained">
                Next
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* View Contractor Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Contractor Details</DialogTitle>
          <DialogContent>
            {selectedContractor && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                      <Typography variant="body2">
                        <strong>Company:</strong>{" "}
                        {selectedContractor.companyName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Registration:</strong>{" "}
                        {selectedContractor.registrationNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>GST:</strong> {selectedContractor.gstNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>PAN:</strong> {selectedContractor.panNumber}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        License Information
                      </Typography>
                      <Typography variant="body2">
                        <strong>License No:</strong>{" "}
                        {selectedContractor.licenseDetails?.laborLicenseNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Issued By:</strong>{" "}
                        {selectedContractor.licenseDetails?.licenseIssuedBy}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Expiry:</strong>{" "}
                        {selectedContractor.licenseDetails?.licenseExpiryDate
                          ? dayjs(
                              selectedContractor.licenseDetails
                                .licenseExpiryDate
                            ).format("DD/MM/YYYY")
                          : "N/A"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenViewDialog(false);
                handleEditContractor(selectedContractor);
              }}
            >
              Edit Contractor
            </Button>
          </DialogActions>
        </Dialog>

        {/* Login Credentials Modal */}
        <Dialog
          open={showCredentialsModal}
          onClose={closeCredentialsModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckIcon color="success" />
              Contractor Created Successfully!
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Login credentials have been generated for the contractor:
            </Typography>

            {loginCredentials && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 100, fontWeight: "bold" }}
                  >
                    Username:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", flex: 1 }}
                  >
                    {loginCredentials.username}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(loginCredentials.username)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 100, fontWeight: "bold" }}
                  >
                    Email:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", flex: 1 }}
                  >
                    {loginCredentials.email}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(loginCredentials.email)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 100, fontWeight: "bold" }}
                  >
                    Password:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", flex: 1 }}
                  >
                    {loginCredentials.password}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(loginCredentials.password)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 100, fontWeight: "bold" }}
                  >
                    Tenant Domain:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", flex: 1 }}
                  >
                    {localStorage.getItem("tenantDomain") || "N/A"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      copyToClipboard(
                        localStorage.getItem("tenantDomain") || ""
                      )
                    }
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              Please share these credentials with the contractor securely. They
              can use these to log in and complete their profile.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCredentialsModal} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Contractors;
